/**
 * RecordLink — Salesforce-lookup-field-style link to a record page.
 *
 * Per Ean 2026-06-09 ("biggest thing in making it feel alive"): anywhere
 * the UI references a data-backed record (a connector, a model, a prompt,
 * a permission set, an activity row, etc.), the name should read as a
 * lookup field, not as static text. Hover signals it's clickable; click
 * lands on the record page. This is what makes the surface feel like a
 * living database instead of hardcoded labels.
 *
 * Visual:
 *   - Default: inherits surrounding text color (no blue link styling that
 *     would shout). Looks like a name, until you mouse over it.
 *   - Hover: subtle teal tint + dotted underline. Same affordance pattern
 *     SF uses for read-only lookup display.
 *   - Active (visited/hovered with mouse down): full teal.
 *
 * Usage:
 *   <RecordLink to={`/connectors/${slug}`}>Supabase</RecordLink>
 *   <RecordLink to={`/models/${slug}`} muted>Claude Haiku 4.5</RecordLink>
 *
 * Props:
 *   - to: target route (any react-router path)
 *   - children: the label text
 *   - muted: render the default state in muted color (useful when the
 *     surrounding context is muted, e.g., meta rows)
 *   - size: 'sm' shrinks the dotted-underline distance for tight layouts
 *   - title: optional native tooltip
 *
 * Not for: prompt-pick interactions (those click-insert, not navigate),
 * tile-level wrapping (use a button-styled tile instead).
 */

import { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface RecordLinkProps {
  to: string;
  children: ReactNode;
  muted?: boolean;
  size?: 'sm' | 'md';
  title?: string;
  /** Stop click from bubbling to a parent button/onClick handler. Useful
   *  when RecordLink sits inside a card that's itself clickable but we
   *  want the name to be its own click target. */
  stopPropagation?: boolean;
  style?: CSSProperties;
}

export function RecordLink({
  to,
  children,
  muted = false,
  size = 'md',
  title,
  stopPropagation = false,
  style,
}: RecordLinkProps) {
  return (
    <Link
      to={to}
      className={`record-link${muted ? ' record-link--muted' : ''}${size === 'sm' ? ' record-link--sm' : ''}`}
      title={title}
      onClick={(e) => {
        if (stopPropagation) e.stopPropagation();
      }}
      style={style}
    >
      {children}
    </Link>
  );
}
